/**
 * useHotkeys Hook
 * 
 * Manages keyboard shortcuts with scope support.
 * Reusable across modals, downloaders, and other interactive components.
 */

import { useEffect, useCallback, useRef } from 'react';

export type HotkeyHandler = (event: KeyboardEvent) => void;

export interface HotkeyConfig {
  /**
   * Key or key combination (e.g., 'Escape', 'Ctrl+s', 'Alt+Enter')
   */
  key: string;
  /**
   * Handler function
   */
  handler: HotkeyHandler;
  /**
   * Whether the handler should prevent default behavior
   */
  preventDefault?: boolean;
  /**
   * Whether the handler should stop propagation
   */
  stopPropagation?: boolean;
  /**
   * Whether the hotkey is enabled
   */
  enabled?: boolean;
  /**
   * Description for accessibility/debugging
   */
  description?: string;
}

export interface UseHotkeysOptions {
  /**
   * Scope element - hotkeys only work when this element or its children have focus
   */
  scope?: HTMLElement | null;
  /**
   * Whether hotkeys are enabled globally (outside of scope)
   */
  global?: boolean;
  /**
   * Whether to enable hotkeys by default
   */
  enabled?: boolean;
}

export interface UseHotkeysReturn {
  /**
   * Add a hotkey
   */
  addHotkey: (config: HotkeyConfig) => () => void;
  /**
   * Remove a hotkey
   */
  removeHotkey: (key: string) => void;
  /**
   * Clear all hotkeys
   */
  clearHotkeys: () => void;
  /**
   * Enable/disable all hotkeys
   */
  setEnabled: (enabled: boolean) => void;
}

export function useHotkeys(
  initialHotkeys: HotkeyConfig[] = [],
  options: UseHotkeysOptions = {}
): UseHotkeysReturn {
  const { scope, global = true, enabled: defaultEnabled = true } = options;
  
  const hotkeysRef = useRef<Map<string, HotkeyConfig>>(new Map());
  const enabledRef = useRef(defaultEnabled);
  const scopeRef = useRef(scope);

  // Update scope ref when it changes
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);

  const parseKey = useCallback((key: string): {
    key: string;
    ctrl: boolean;
    alt: boolean;
    shift: boolean;
    meta: boolean;
  } => {
    const parts = key.toLowerCase().split('+').map(part => part.trim());
    const modifiers = {
      ctrl: false,
      alt: false,
      shift: false,
      meta: false,
    };
    
    let mainKey = '';
    
    for (const part of parts) {
      switch (part) {
        case 'ctrl':
        case 'control':
          modifiers.ctrl = true;
          break;
        case 'alt':
          modifiers.alt = true;
          break;
        case 'shift':
          modifiers.shift = true;
          break;
        case 'meta':
        case 'cmd':
        case 'command':
          modifiers.meta = true;
          break;
        default:
          mainKey = part;
      }
    }
    
    return { key: mainKey, ...modifiers };
  }, []);

  const matchesKey = useCallback((event: KeyboardEvent, config: HotkeyConfig): boolean => {
    const parsed = parseKey(config.key);
    
    return (
      event.key.toLowerCase() === parsed.key &&
      event.ctrlKey === parsed.ctrl &&
      event.altKey === parsed.alt &&
      event.shiftKey === parsed.shift &&
      event.metaKey === parsed.meta
    );
  }, [parseKey]);

  const isInScope = useCallback((event: KeyboardEvent): boolean => {
    if (!scopeRef.current) return global;
    
    const target = event.target as HTMLElement;
    return scopeRef.current.contains(target) || scopeRef.current === target;
  }, [global]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabledRef.current) return;
    
    // Check if event is in scope
    if (!isInScope(event)) return;
    
    // Find matching hotkey
    for (const [key, config] of hotkeysRef.current) {
      if (config.enabled !== false && matchesKey(event, config)) {
        if (config.preventDefault) {
          event.preventDefault();
        }
        if (config.stopPropagation) {
          event.stopPropagation();
        }
        config.handler(event);
        break; // Only handle the first matching hotkey
      }
    }
  }, [isInScope, matchesKey]);

  const addHotkey = useCallback((config: HotkeyConfig): (() => void) => {
    const key = config.key.toLowerCase();
    hotkeysRef.current.set(key, config);
    
    // Return cleanup function
    return () => {
      hotkeysRef.current.delete(key);
    };
  }, []);

  const removeHotkey = useCallback((key: string) => {
    hotkeysRef.current.delete(key.toLowerCase());
  }, []);

  const clearHotkeys = useCallback(() => {
    hotkeysRef.current.clear();
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    enabledRef.current = enabled;
  }, []);

  // Add initial hotkeys
  useEffect(() => {
    const cleanupFunctions: (() => void)[] = [];
    
    for (const config of initialHotkeys) {
      const cleanup = addHotkey(config);
      cleanupFunctions.push(cleanup);
    }
    
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [addHotkey, initialHotkeys]);

  // Add event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    addHotkey,
    removeHotkey,
    clearHotkeys,
    setEnabled,
  };
}

/**
 * Common hotkey configurations for modals and downloaders
 */
export const commonHotkeys = {
  close: (handler: () => void): HotkeyConfig => ({
    key: 'Escape',
    handler: () => handler(),
    preventDefault: true,
    description: 'Close modal or cancel operation',
  }),
  
  closeAlt: (handler: () => void): HotkeyConfig => ({
    key: 'q',
    handler: () => handler(),
    preventDefault: true,
    description: 'Close modal (alternative)',
  }),
  
  next: (handler: () => void): HotkeyConfig => ({
    key: 'ArrowRight',
    handler: () => handler(),
    preventDefault: true,
    description: 'Go to next item',
  }),
  
  previous: (handler: () => void): HotkeyConfig => ({
    key: 'ArrowLeft',
    handler: () => handler(),
    preventDefault: true,
    description: 'Go to previous item',
  }),
  
  first: (handler: () => void): HotkeyConfig => ({
    key: 'Home',
    handler: () => handler(),
    preventDefault: true,
    description: 'Go to first item',
  }),
  
  last: (handler: () => void): HotkeyConfig => ({
    key: 'End',
    handler: () => handler(),
    preventDefault: true,
    description: 'Go to last item',
  }),
  
  zoomIn: (handler: () => void): HotkeyConfig => ({
    key: '+',
    handler: () => handler(),
    preventDefault: true,
    description: 'Zoom in',
  }),
  
  zoomOut: (handler: () => void): HotkeyConfig => ({
    key: '-',
    handler: () => handler(),
    preventDefault: true,
    description: 'Zoom out',
  }),
  
  reset: (handler: () => void): HotkeyConfig => ({
    key: 'r',
    handler: () => handler(),
    preventDefault: true,
    description: 'Reset view',
  }),
  
  toggleSlideshow: (handler: () => void): HotkeyConfig => ({
    key: ' ',
    handler: () => handler(),
    preventDefault: true,
    description: 'Toggle slideshow',
  }),
} as const;
